import { Box, Checkbox, IconButton, styled, Tooltip, Typography } from '@mui/material'
import {
  GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
  GridColumns,
  GridNativeColTypes,
  GridRenderCellParams,
  GridRowId,
  GridRowParams,
  gridDetailPanelExpandedRowsContentCacheSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro'
import React, { useCallback } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdExpandMore } from 'react-icons/md'
import GenericTable from '../../../components/table/GenericTable'
import { MainButton } from '../../../components/MainButton'
import { GlobalDocumentVersionTable } from './GlobalDocumentVersionTable'
import { staticGlobalDocumentColumns } from './constants'
import { useDialogCtx } from '../../../hooks/context-hooks'
import { GlobalDocumentCreateDialog } from './GlobalDocumentCreateDialog'
import { globalDocumentEndpoints, invalidateGlobalDocument, useDeleteDocument } from '../../../api/global-documents'
import { GlobalDocumentVersionCreateDialog } from './GlobalDocumentVersionCreateDialog'
import { AlertDialog } from '../../../show-dialog'
import { toast } from 'react-toastify'
import { GlobalDocumentRuleContainerDialog } from './GlobalDocumentRuleContainerDialog'
import { GlobalDocumentEditDialog } from './GlobalDocumentEditDialog'
import { IGlobalDocument } from '../../../generated-types/global-documents'
import { Launch } from '@mui/icons-material'
import { invalidateDocumentCategoryCache } from '../../../api/document-category'
import { invalidateDocument } from '../../../api/document'
import { invalidateSupplierCache } from 'src/api/supplier'
import { GlobalDocumentApprovalStatus } from 'src/screens/customer/global-documents/GlobalDocumentApprovalStatus'
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { GlobalDocumentType } from 'src/enums'

const VersionPanelToggle = (props: Pick<GridRenderCellParams, 'id' | 'value'>) => {
  const { id, value: isExpanded } = props
  const apiRef = useGridApiContext()
  const { t } = useTranslation(['global-documents', 'common'])

  const contentCache = useGridSelector(apiRef, gridDetailPanelExpandedRowsContentCacheSelector)

  const hasDetail = React.isValidElement(contentCache[id])

  return (
    <Tooltip title={`${t('view-versions', { ns: 'global-documents' })}`}>
      <IconButton
        size="medium"
        tabIndex={-1}
        disabled={!hasDetail}
        aria-label={isExpanded ? 'Close' : 'Open'}
        sx={{
          transform: `rotateZ(${isExpanded ? 180 : 0}deg)`,
          transition: theme =>
            theme.transitions.create('transform', {
              duration: theme.transitions.duration.shortest,
            }),
        }}
      >
        <MdExpandMore fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}

export interface GlobalDocumentsTableProps {
  isSuperAdmin?: boolean
  supplierId?: number
  customerId?: EntityId | undefined
  height: string | number
  isDeletedSupplier?: boolean
  globalDocumentType?: GlobalDocumentType
}

export const GlobalDocumentsTable: React.FC<GlobalDocumentsTableProps> = ({
  isSuperAdmin = false,
  supplierId,
  customerId,
  height,
  isDeletedSupplier,
  globalDocumentType,
}) => {
  const { mutateAsync } = useDeleteDocument()
  const { t } = useTranslation(['global-documents', 'common'])
  const { showDialog, openFilePreviewDialog } = useDialogCtx()
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = React.useState<GridRowId[]>([])

  const handleDetailPanelExpandedRowIdsChange = React.useCallback((newIds: GridRowId[]) => {
    setDetailPanelExpandedRowIds(newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds)
  }, [])

  const handleAddDocument = useCallback(() => {
    showDialog(GlobalDocumentCreateDialog, {
      title: t('add-document', { ns: 'global-documents' }),
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
      componentProps: {
        isSuperAdmin: isSuperAdmin,
        supplierId: supplierId,
        customerId: customerId,
        globalDocumentType: globalDocumentType,
      },
    })
  }, [showDialog, supplierId, t, isSuperAdmin, globalDocumentType])

  const handleEditDocument = (row: IGlobalDocument) => {
    showDialog(GlobalDocumentEditDialog, {
      title: t('edit-document', { ns: 'global-documents' }),
      opts: {
        maxWidth: 'md',
        fullWidth: true,
      },
      componentProps: {
        isSuperAdmin: isSuperAdmin,
        documentId: row.id,
        initialValues: row,
        customerId: customerId,
      },
    })
  }

  const handleDeleteDocument = useCallback(
    async (id: EntityId) => {
      showDialog(AlertDialog, {
        componentProps: {
          title: t('delete-document', { ns: 'global-documents' }),
          text: t('areYouSureDocument', { ns: 'global-documents' }),
          onConfirm: async pop => {
            await mutateAsync(id).then(
              () => {
                toast(t('successfullyDeletedDocument', { ns: 'global-documents' }), { type: 'success' })
                invalidateGlobalDocument.getDocuments()
                customerId && invalidateDocumentCategoryCache.getDocumentCategoriesWithDocumentCountByKey()
                customerId && invalidateDocument.getDocuments(customerId)
                customerId && invalidateSupplierCache.getCustomerSuppliers(customerId)
                invalidateSupplierCache.getAdminSuppliers()
                invalidateDocument.getDocumentsForApproval()
                pop(true)
              },
              () => {
                toast(t('deletingError', { ns: 'common' }), { type: 'error' })
                pop(false)
              },
            )
          },
        },
      })
    },
    [t],
  )
  const handleAddNewVersion = useCallback(
    (documentId, documentTitle) => {
      showDialog(GlobalDocumentVersionCreateDialog, {
        title: t('add-version', { ns: 'global-documents' }),
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
        componentProps: {
          documentId: documentId,
          documentTitle: documentTitle,
          supplierId: supplierId,
          customerId: customerId,
        },
      })
    },
    [showDialog, supplierId, t],
  )

  const handleAddRule = useCallback(
    (row: GridRowParams<any>) => {
      showDialog(GlobalDocumentRuleContainerDialog, {
        title: t('document-rules', { ns: 'global-documents' }),
        opts: {
          maxWidth: 'lg',
          fullWidth: true,
        },
        componentProps: {
          globalDocumentId: row.id,
          customerId: customerId,
          globalDocumentType: globalDocumentType,
        },
      })
    },
    [customerId, showDialog, t, globalDocumentType],
  )

  const handleOpenReadStatus = useCallback(
    async row => {
      showDialog(GlobalDocumentApprovalStatus, {
        title: t('user-read-status', { ns: 'document' }),
        componentProps: {
          documentId: row.id,
          customerId: customerId,
          pop: () => {},
        },
        opts: {
          maxWidth: 'md',
          fullWidth: true,
        },
      })
    },
    [showDialog, customerId],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        flex: 0.2,
        align: 'center',
        renderCell: params => <VersionPanelToggle id={params.id} value={params.value} />,
      },
      {
        field: 'isActive',
        headerName: t('active', { ns: 'common' }),
        flex: 0.4,
        type: 'checkbox',
        align: 'center',
        sortable: false,
        renderCell: params => {
          return (
            <>
              <Checkbox disabled checked={!!params.row.isActive} inputProps={{ 'aria-label': 'controlled' }} value="" />
            </>
          )
        },
      },
      {
        field: 'title',
        headerName: t('title', { ns: 'common' }),
        flex: 2,
        renderCell: ({ row }) => {
          return (
            <StyledTypography onClick={() => openFilePreviewDialog(row.documentUrl)}>
              {row.title}
              <Launch />
            </StyledTypography>
          )
        },
      },
      ...(staticGlobalDocumentColumns(t) as GridColumns),
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        headerName: t('actions', { ns: 'common' }),
        width: 15,
        flex: 2.5,
        getActions: ({ row }) =>
          [
            <Box
              key={'add-version'}
              paddingLeft={1}
              width={globalDocumentType === GlobalDocumentType.Locality ? '200px' : 'auto'}
              display="flex"
              justifyContent="flex-end"
            >
              <MainButton
                variant="contained"
                sx={{ minWidth: '120px', minHeight: '24px', textTransform: 'none' }}
                onClick={() => handleAddNewVersion(row.id, row.title)}
              >
                {t('add-version', { ns: 'global-documents' })}
              </MainButton>
            </Box>,
            // Locality global documents from customer admin side has only one rule for now.
            // That is added manually by backend code currently.
            // If user want to add rules by selecting localities, That part need to to be implemented in future.
            globalDocumentType != GlobalDocumentType.Locality && (
              <Box key={'add-rule'} paddingLeft={1}>
                <MainButton
                  variant="contained"
                  onClick={() => handleAddRule(row)}
                  sx={{ minWidth: '120px', minHeight: '24px', textTransform: 'none' }}
                >
                  {t('add-rule', { ns: 'global-document-rules' })}
                </MainButton>
              </Box>
            ),
            <GridActionButton
              key="edit"
              toolTipTitle={t('edit', { ns: 'common' })}
              buttonIcon={<EditIcon />}
              onClick={() => handleEditDocument(row as IGlobalDocument)}
              width="40px"
              height="40px"
            />,
            <GridActionButton
              key="delete"
              toolTipTitle={t('delete', { ns: 'common' })}
              buttonIcon={<DeleteIcon />}
              onClick={() => handleDeleteDocument(row.id)}
              width="40px"
              height="40px"
            />,
            !isSuperAdmin ? (
              <GridActionButton
                key="read-status"
                toolTipTitle={t('user-read-status', { ns: 'document' })}
                buttonIcon={<ChecklistRtlIcon />}
                onClick={() => handleOpenReadStatus(row)}
                disabled={!row.isReadApprovalRequired}
                width="40px"
                height="40px"
              />
            ) : null,
          ].filter(Boolean),
      },
    ],
    [t, handleAddNewVersion, handleAddRule, handleEditDocument, handleDeleteDocument, handleOpenReadStatus],
  )

  return (
    <GenericTable
      id="global-document-table"
      columns={columns}
      enableServerPagination={
        supplierId != undefined ||
        globalDocumentType == GlobalDocumentType.Locality ||
        globalDocumentType == GlobalDocumentType.AdminLocality
      }
      queryUrl={globalDocumentEndpoints.getDocuments()}
      queryParams={{ supplierId, customerId, globalDocumentType }}
      pageSize={10}
      getRowHeight={() => 'auto'}
      rowColor="#e5e4e2"
      enableToolbar={true}
      toolbarOptions={[
        {
          label: t('add-document', { ns: 'global-documents' }),
          Component: (
            <MainButton
              disabled={
                globalDocumentType !== GlobalDocumentType.Locality &&
                globalDocumentType !== GlobalDocumentType.AdminLocality &&
                (supplierId == undefined || isDeletedSupplier === true)
              }
              onClick={handleAddDocument}
            >
              {t('add-document', { ns: 'global-documents' })}
            </MainButton>
          ),
        },
      ]}
      getDetailPanelContent={({ row }) => {
        return <GlobalDocumentVersionTable globalDocumentId={row.id} customerId={customerId} />
      }}
      getDetailPanelHeight={() => 'auto'}
      detailPanelExpandedRowIds={detailPanelExpandedRowIds}
      onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
      noRowsMessage={
        supplierId == undefined
          ? t('supplier-error', { ns: 'global-documents' })
          : t('no-documents', { ns: 'global-documents' })
      }
      onCellClick={({ row, field }) => {
        if (field !== 'actions' && field !== 'title' && field != '__detail_panel_toggle__') handleAddRule(row)
      }}
      autoHeight={false}
      height={height}
    />
  )
}

export const StyledTypography = styled(Typography)(({ theme }) => ({
  cursor: 'pointer',
  color: getThemeColor(theme, MUIThemeColors.primaryMain),
  '&:hover': { textDecoration: 'underline' },
}))
